import qs from 'query-string'
import React, { useCallback, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import pclnExperimentation from '@pcln/experimentation'
import { Link as RouterLink, withRouter } from 'react-router-dom'
import {
  BlockLink,
  Box,
  Card,
  Divider,
  Flex,
  Heading,
  Image,
  Text,
  Link
} from 'pcln-design-system'
import moment from 'moment'
import { equals, isEmpty } from 'ramda'
import Modal from '../promos/hotels-promo-collection/Modal'
import HotelsSearchContainer from '../../containers/hotels/HotelsSearchContainer'
import SOPQFlightFormContainer from '../search-forms/SOPQFlightFormContainer'
import HotelsSearchForm from '../search-forms/HotelsSearchForm'
import FlightFormContainer from '../search-forms/FlightsSearchContainer'
import RentalCarSearchForm from '../search-forms/RentalCarSearchForm'
import RentalCarSearchContainer from '../search-forms/RentalCarsSearchContainer'
import SearchBoxHeader from './SearchBoxHeader'
import { handleSubmitForHotels } from '../search-forms/utils/hotels-url-formatters'
import * as PCLNAnalytics from '../../utils/PCLN-Analytics'
import packagesSearch from '../../utils/packages-search'
import { Fader, RotatingText } from '../../shared'
import { fireFormElementClick } from '../../actions/GoogleAnalyticsClickActions'
import FlightSearchActions from '../../actions/FlightSearchActions'
import VacationPackageSearchActions from '../../actions/VacationPackageSearchActions'
import {
  AIR_SEARCH_DATA,
  ANALYTICS_ACTION_FLIGHTS_SEARCH,
  ANALYTICS_CATEGORY,
  ANALYTICS_ACTION_FLIGHTS_SEARCH_BUTTON,
  CAR_SEARCH_DATA,
  CRUISE_PRODUCT_TYPE,
  FLIGHT_AND_CAR,
  FLIGHT_PRODUCT_TYPE,
  HOTEL_AND_CAR,
  HOTEL_FLIGHT_CAR,
  HOTEL_PRODUCT_TYPE,
  RENTAL_CAR_PRODUCT_TYPE,
  VACATION_PACKAGE_PRODUCT_TYPE
} from '../../constants/Constants'
import rentalCarContent from '../../constants/rentalCar-banner-content'
import withAnalytics from './withAnalytics'
import withTypeAhead from './withTypeAhead'
import Packages from './Packages'
import Cruises from './Cruises'
import flightBannerContent from '../../constants/flight-banner-content'
import { BootstrapDataContext, ExperimentContext, FormVisualStateContainer } from '../../contexts'
import { useContainer } from 'unstated-next'
import { handleTypeahead } from '../search-forms/utils'
import useHotelsPrePop from '../../containers/hotels/hooks/useHotelsPrePop'
import {
  persistSearchData
} from '../../utils/local-forage'
import { useCurrentLocation } from '../hooks/useCurrentLocation'
import { ArrowRight } from 'pcln-icons'

const trackClick = name => (label, action = 'Field Click') => {
  const dataVal = name ? `${name} - ${label}` : `${label}`
  fireFormElementClick({
    category: 'PCLN_HOME',
    action: action,
    data: dataVal
  })
}

const trackHotelSearchBarLink = () => (label = 'Trips link', action = 'HP Hotels Search Bar') => {
  trackClick()(label, action)
}

const trackSelectFocus = name => (label = 'room select') => {
  trackClick(name)(label)
}

const trackTypeaheadFocus = name => (label = 'typeahead', action) => {
  trackClick(name)(label, action)
}

const trackDateFocus = name => (label = 'datefield') => {
  trackClick(name)(label)
}

const BumperLink = BlockLink.withComponent(RouterLink)
const HOTEL_TAB_KEY = 'hotels'
const FLIGHT_TAB_KEY = 'flights'

const parseQuery = location => {
  const { tab } = qs.parse(location.search)
  return tab || HOTEL_TAB_KEY
}

// TODO: d-home local modal container style hack;
//       look into adjusting home-components Modal component to improve
export const ModalContainer = styled(Box)`
  > div {
    max-height: 100%;
    overflow-y: auto;
  }
`

// TODO: Look into improving by avoid using document.getElementById
export const handleModalContainerOnClick = options => {
  const { props, event, modalID, tab } = options
  const modal = document.getElementById(modalID)
  const isClickOutsideOfModal = modal && !modal.contains(event.target)

  if (isClickOutsideOfModal) {
    event.preventDefault()
    if (Array.isArray(props.history)) {
      props.history.push({ search: `?${qs.stringify({ tab: tab })}` })
    }
  }
}

export const HotelsTypeAheadForm = withTypeAhead(HotelsSearchForm)

const initialTypeAheadConfig = {
  endpoint: 'hotels',
  keys: ['location']
}

const HotelsSearchContainerWrapper = props => {
  const currentLocation = useCurrentLocation(props.currentLocation)
  const {
    location,
    isStickyInEffect,
    onSubmit
  } = props

  const [typeAhead, setTypeAhead] = useState(initialTypeAheadConfig)
  const initialValuesWithPrePop = useHotelsPrePop(location)

  const sendSubmitAnalyticsEvent = (values, isSOPQ = false) => {
    const location = values.locationObject
      ? values.locationObject.itemName
      : ''

    if (!isSOPQ && location && !values.startDate && !values.endDate) {
      if (isStickyInEffect) {
        PCLNAnalytics.send({
          action: 'Hotel Sticky Search Button',
          data: 'Valid'
        })

        return PCLNAnalytics.send({
          action: 'HotelStickySearch_NoDateSearch_rtl',
          data: 'Y'
        })
      } else {
        PCLNAnalytics.send({
          action: 'Hotel Search Button',
          data: 'Valid'
        })

        return PCLNAnalytics.send({
          action: 'Hotel_NoDateSearch_rtl',
          data: 'Y'
        })
      }
    }

    if (props.isStickyInEffect) {
      PCLNAnalytics.send({
        action: 'Hotel Sticky Search Button',
        data: location
      })

      PCLNAnalytics.send({
        action: 'Hotel Sticky Search Button',
        data: 'Valid'
      })
    } else {
      PCLNAnalytics.send({
        action: 'Hotel Search Button',
        data: location
      })

      PCLNAnalytics.send({
        action: 'Hotel Search Button',
        data: 'Valid'
      })
    }
  }

  const changeTypeAheadConfig = useCallback(key => {
    if (key === 'hotels') {
      setTypeAhead(initialTypeAheadConfig)
    } else if (key === 'packages') {
      setTypeAhead({ endpoint: 'vacations', keys: ['startLocation', 'location'] })
    }
  }, [])

  return (
    <Box>
      <Box p={3}>
        <HotelsSearchContainer
          {...props}
          onSubmit={values => handleSubmitForHotels({
            onSubmit,
            sendSubmitAnalyticsEvent,
            values
          })}
          enableReinitialize
          handleRoomSelectFocus={trackSelectFocus('Hotels')}
          handleTypeaheadFocus={trackTypeaheadFocus('Hotels')}
          handleDateFocus={trackDateFocus('Hotels')}
          handleTravelerSelectionOnFocus={trackTypeaheadFocus('Hotels')}
          initialValues={initialValuesWithPrePop}
          currentLocationProps={currentLocation}
          render={props => (
            <HotelsTypeAheadForm
              {...props}
              changeTypeAheadConfig={changeTypeAheadConfig}
              typeAhead={typeAhead}
            />
          )}
        />
      </Box>
      <Divider my={0} />
      <Box
        px={3}
        py={3}
        style={{
          height: 48
        }}
        color='background.light'
      >
        <Flex justifyContent='center' >
          <Link
            href='/trips'
            onClick={() => trackHotelSearchBarLink()()}
          >
            <Text fontWeight={700} mb={2} mr={2} color='primary'>
              Planning a getaway? Save on nearby escapes
            </Text>
          </Link>
          <ArrowRight pb={1} />
        </Flex>
      </Box>
    </Box>
  )
}

HotelsSearchContainerWrapper.propTypes = {
  initialDateValues: PropTypes.shape({ startDate: PropTypes.string, endDate: PropTypes.string }),
  hotelFormSavedValues: PropTypes.object,
  onSubmit: PropTypes.func,
  isStickyInEffect: PropTypes.bool,
  location: PropTypes.object,
  tab: PropTypes.string,
  currentLocation: PropTypes.object
}

const Hotels = withAnalytics({
  action: 'Hotel Search',
  events: {
    location: values => values.locationObject && values.locationObject.type,
    startDate: 'CheckIn',
    endDate: 'CheckOut',
    roomCount: values => `${values.roomCount} Rooms`,
    adultCount: values => `${values.adultCount} Adults`,
    childrenCount: values => `${values.childrenCount} Children`
  }
})(HotelsSearchContainerWrapper)

// drive/search/ JFK/JFK/20180621-12:00/20180623-12:00
// placeholder: move this to home-components
function formatCarsLocation (location) {
  if (!location) return null
  if (location.type === 'AIRPORT') {
    return location.id
  }
  return encodeURI(location.itemName)
}

function formatCarsPartnerLocation (location) {
  if (!location) return null
  if (location.type === 'AIRPORT') {
    return location.id
  }
  if (location.type === 'PARTNER_LOC') {
    return location.id
  }
  return encodeURI(location.itemName)
}

export function formatParameters ({
  oneWay = false,
  startLocationObject,
  endLocationObject,
  startDate,
  endDate,
  startTime = '12:00',
  endTime = '12:00',
  formatLocation = formatCarsLocation
}) {
  const startLocation = formatLocation(startLocationObject)
  const endLocation = oneWay ? formatLocation(endLocationObject) : ''
  const formattedStartDate = moment(startDate).format('YYYYMMDD')
  const formattedEndDate = moment(endDate).format('YYYYMMDD')

  return [
    '/drive/search/r',
    startLocation,
    oneWay && endLocation ? endLocation : startLocation,
    formattedStartDate + '-' + startTime,
    formattedEndDate + '-' + endTime,
    'list'
  ].join('/')
}

const RCTypeAheadForm = withTypeAhead(RentalCarSearchForm)

const RentalCars = withAnalytics({
  action: 'Car Search',
  events: {
    startLocation: values =>
      values.startLocationObject && `Pick-up_${values.startLocation}`,
    startDate: 'Pick-up Date',
    startTime: values => `Pick-up time ${values.startTime}`,
    endLocation: values =>
      values.endLocationObject && `Drop-off_${values.endLocation}`,
    endDate: 'Drop-off Date',
    endTime: values => `Drop-off time ${values.endTime}`,
    oneWay: values =>
      values.oneWay
        ? 'different drop-off-location'
        : 'uncheck different drop-off'
  }
})(
  class extends React.Component {
    static contextType = ExperimentContext

    static propTypes = {
      initValues: PropTypes.func,
      onSubmit: PropTypes.func,
      carsFormSavedValues: PropTypes.object,
      initialDateValues: PropTypes.object
    }

    constructor (props) {
      super(props)

      const initialValues = {
        startLocation: '',
        endLocation: '',
        startDate: this.props.initialDateValues.startDate || '',
        endDate: this.props.initialDateValues.endDate || '',
        roomCount: 1,
        adultCount: 2,
        childrenCount: 0,
        childrenAges: [],
        startTime: '12:00',
        endTime: '12:00',
        oneWay: false,
        tripType: 'AH'
      }

      const initialTypeAheadConfig = {
        endpoint: 'cars',
        keys: ['startLocation', 'endLocation']
      }

      this.state = {
        rentalCarsInitialValues: props.initValues(
          props.carsFormSavedValues,
          initialValues,
          props.initialDateValues
        ),
        typeAheadConfig: initialTypeAheadConfig
      }
    }

    handleSubmit = (values) => {
      const hotelPlusFlight = values.tripType === HOTEL_FLIGHT_CAR
      const hotelAdded = hotelPlusFlight || values.tripType === HOTEL_AND_CAR
      const flightAdded = hotelPlusFlight || values.tripType === FLIGHT_AND_CAR

      persistSearchData(CAR_SEARCH_DATA, values)

      if (hotelAdded || flightAdded) {
        const action = hotelPlusFlight
          ? 'Car Search Button (with Hotel and Flight)' : hotelAdded
            ? 'Car Search Button (with Hotel)' : 'Car Search Button (with Flight)'
        PCLNAnalytics.send({
          action,
          data: 'Valid'
        })
        if (values.endLocationObject) {
          values.endLocationObject.name = values.endLocationObject.itemName
        }
        if (values.startLocationObject) {
          values.startLocationObject.name = values.startLocationObject.itemName
        }
        return packagesSearch({ ...values })
      }

      const { onSubmit } = this.props
      const url = formatParameters({ ...values, ...{ formatLocation: formatCarsPartnerLocation } })

      onSubmit(url)

      PCLNAnalytics.send({
        action: 'Car Search Button',
        data: 'Valid'
      })
      // todo: look into invalid submit clicks
    }

    handleTypeAheadConfigChange = (key) => {
      let config = {
        endpoint: 'cars',
        keys: ['startLocation', 'endLocation']
      }
      if (key === 'packages') {
        config = { endpoint: 'vacations', keys: ['startLocation', 'rcLocation'] }
      }
      this.setState({ typeAheadConfig: config })
    }

    render () {
      return (
        <Box>
          <Box p={3} pt={0}>
            <RentalCarSearchContainer
              {...this.props}
              onSubmit={this.handleSubmit}
              initialValues={this.state.rentalCarsInitialValues}
              savedValues={this.props.carsFormSavedValues}
              render={props => (
                <RCTypeAheadForm
                  {...props}
                  onTypeAheadConfigChange={this.handleTypeAheadConfigChange}
                  typeAhead={this.state.typeAheadConfig}
                />
              )}
            />
          </Box>
          <Divider my={0} />
          <Flex
            px={3}
            py={3}
            alignItems='center'
            justifyContent='center'
            style={{
              height: 48,
              borderBottomRightRadius: '6px',
              borderBottomLeftRadius: '6px'
            }}
            color='background.light'
          >
            <Fader duration={6000}>
              <Text fontSize={1} textAlign='center' color='primary'>
                {rentalCarContent.bannerA}
              </Text>
              <Text fontSize={1} textAlign='center'>
                <Flex alignItems='center' justifyContent='center'>
                  {rentalCarContent.bannerB}
                  <Box ml={1} />
                  <Image src={rentalCarContent.image} width='343' height='25' />
                </Flex>
              </Text>
            </Fader>
          </Flex>
        </Box>
      )
    }
  }
)

const flightsCategory = ANALYTICS_CATEGORY
const flightsAction = ANALYTICS_ACTION_FLIGHTS_SEARCH
const flightsButtonAction = ANALYTICS_ACTION_FLIGHTS_SEARCH_BUTTON
// TODO: will address form submission urls in a follow-up to clean things up and use the new packagesUrl etc

// TODO: all these handle*** functions to support Flight form can refactor into
//       the class pattern, to be consistent with other forms,
//       when the Flight form splits.

// TODO: will address form submission urls in a follow-up to clean things up
// and use the new packagesUrl etc
const handleFormSubmission = (request = null, isSOPQSearch = false) => {
  const isNewForm = true
  const { isValid, finalErrors, productType, tripType } = request
  if (isValid) {
    persistSearchData(AIR_SEARCH_DATA, request)
  }
  const isRoundTrip = tripType === 'round-trip'
  const isProdTypeShopSearch = productType && /STAY|DRIVE/.test(productType)

  // TODO: Follow up on this to remove the legacy form builder code...
  if (isRoundTrip && isProdTypeShopSearch && isValid) {
    const isFlightsHomeSearch = true
    VacationPackageSearchActions.searchRetail(request, isFlightsHomeSearch)

    const data = 'Valid'
    fireFormElementClick({
      category: flightsCategory,
      action: flightsButtonAction,
      data
    })
  } else if (isSOPQSearch && isValid) {
    FlightSearchActions.searchSOPQ(request, isNewForm)

    const data = 'Valid'
    fireFormElementClick({
      category: flightsCategory,
      action: flightsButtonAction,
      data
    })
  } else if (isValid) {
    FlightSearchActions.searchRetail(request, isNewForm)

    const data = 'Valid'
    fireFormElementClick({
      category: flightsCategory,
      action: flightsButtonAction,
      data
    })
  } else {
    const errKeys = Object.keys(finalErrors)
    let errMsg = ''

    errKeys.forEach((ekey, idx) => {
      if (idx === 0) {
        errMsg = ekey
      } else {
        errMsg += ` | ${ekey}`
      }
    })
    const data = `Invalid - ${errMsg}`
    fireFormElementClick({
      category: flightsCategory,
      action: flightsButtonAction,
      data
    })
  }
}

const handleTripTypeChange = type => {
  const tripTypeGADictionary = {
    'one-way': 'OW',
    'round-trip': 'RT',
    'multi-destination': 'MD'
  }
  const data = tripTypeGADictionary[type]
  fireFormElementClick({
    category: flightsCategory,
    action: flightsAction,
    data
  })

  trackClick('Flights')('trip type click')
}

const handleTravelerChange = returnedData => {
  const travelerGADictionary = {
    ADULTS: 'adults',
    YOUTHS: 'youths',
    CHILDREN: 'children',
    LAP_INFANTS: 'lap infant(s)'
  }

  const data = `${returnedData.count} ${travelerGADictionary[returnedData.travelerType]}`
  fireFormElementClick({
    category: flightsCategory,
    action: flightsAction,
    data
  })
}

const handleCabinClassChange = returnedData => {
  const data = returnedData.cabinClass
  fireFormElementClick({
    category: flightsCategory,
    action: flightsAction,
    data
  })

  trackClick('Flights')('cabin class click')
}

const handleProductTypeChange = returnedData => {
  const data = returnedData.productType
  fireFormElementClick({
    category: flightsCategory,
    action: flightsAction,
    data
  })

  trackClick('Flights')('product type click')
}

const FLT_ED_MODAL_ID = 'flt-ed-modal'

const Flights = class extends React.Component {
  static contextType = ExperimentContext
  static propTypes = {
    history: PropTypes.object,
    location: PropTypes.object
  }

  handleCalendarChange = returnedData => {
    if (returnedData.departureDate) {
      const data = 'Depart Date'
      fireFormElementClick({
        category: flightsCategory,
        action: flightsAction,
        data
      })
    }

    if (returnedData.arrivalDate) {
      const data = 'Return Date'
      fireFormElementClick({
        category: flightsCategory,
        action: flightsAction,
        data
      })
    }
  }

  render () {
    const { history, location } = this.props
    const search = qs.parse(location.search)
    return (
      <Box>
        <Box p={3} width={1}>
          <FlightFormContainer
            handleTripTypeChange={handleTripTypeChange}
            handleCalendarChange={this.handleCalendarChange}
            handleTravelerChange={handleTravelerChange}
            handleCabinClassChange={handleCabinClassChange}
            handleProductTypeChange={handleProductTypeChange}
            handleFormSubmission={request => {
              handleFormSubmission(request, false)
            }}
            handleCalendarOnFocus={trackDateFocus('Flights')}
            handleOnFocus={trackTypeaheadFocus('Flights')}
            handleProductFocus={trackSelectFocus('Flights')}
            handleTravelerSelectionOnFocus={trackTypeaheadFocus('Flights')}
            {...this.props}
          />
        </Box>
        <Divider my={0} />
        <Box px={3} py={3}
          style={{
            height: 48,
            borderBottomRightRadius: '6px',
            borderBottomLeftRadius: '6px'
          }}
          color='background.light'
        >
          <BumperLink
            to={{
              search:
                '?' +
                qs.stringify({
                  ...search,
                  modal: 'express-deals'
                })
            }}
          >
            <RotatingText
              rotatingBannerA={flightBannerContent.bannerA}
              rotatingBannerB={flightBannerContent.bannerB}
              isImageIncluded
              image={flightBannerContent.image}
              imageWebP={flightBannerContent.imageWebP}
              height={flightBannerContent.height}
              imageBackgroundColor={flightBannerContent.imageBackgroundColor}
            />
          </BumperLink>
        </Box>
        {search.modal === 'express-deals' && (
          <ModalContainer
            onClick={e => {
              const opts = {
                props: this.props,
                event: e,
                modalID: FLT_ED_MODAL_ID,
                tab: FLIGHT_TAB_KEY
              }
              handleModalContainerOnClick(opts)
            }}
          >
            <Modal
              id={FLT_ED_MODAL_ID}
              show
              onClose={e => {
                history.push({
                  search: `?${qs.stringify({ tab: FLIGHT_TAB_KEY })}`
                })
              }}
            >
              <Heading fontSize={5} fontWeight={400} mb={2}>
                Search For Express Deals <sup>®</sup> Flights
              </Heading>
              <Text mb={4}>Save up to 50% on last-minute travel!</Text>
              <SOPQFlightFormContainer
                handleTypeahead={handleTypeahead}
                handleTripTypeChange={handleTripTypeChange}
                handleCalendarChange={this.handleCalendarChange}
                handleTravelerChange={handleTravelerChange}
                handleCalendarOnFocus={trackDateFocus('Flights SOPQ')}
                handleOnFocus={trackTypeaheadFocus('Flights SOPQ')}
                handleProductFocus={trackSelectFocus('Flights SOPQ')}
                handleFormSubmission={request => {
                  const isSOPQSearch = true
                  handleFormSubmission(request, isSOPQSearch)
                }}
              />
            </Modal>
          </ModalContainer>
        )}
      </Box>
    )
  }
}

const SearchForms = (props) => {
  const {
    referral = {}
  } = useContext(BootstrapDataContext)
  const { isStickyInEffect } = useContainer(FormVisualStateContainer)
  const { tab } = props

  switch (tab) {
    case RENTAL_CAR_PRODUCT_TYPE:
      return <RentalCars {...props}
        handleTypeaheadOnFocus={trackTypeaheadFocus('RentalCars')}
        handleCalendarOnFocus={trackDateFocus('RentalCars')}
        handleSelectOnFocus={trackSelectFocus('RentalCars')}
        handleTrackingClick={trackClick('RentalCars')}
        handleTravelerSelectionOnFocus={trackTypeaheadFocus('RentalCars')}
      />
    case FLIGHT_PRODUCT_TYPE:
      return <Flights
        {...props}
      />
    case VACATION_PACKAGE_PRODUCT_TYPE:
      return <Packages {...props}
        handleTypeaheadOnFocus={trackTypeaheadFocus('Packages')}
        handleCalendarOnFocus={trackDateFocus('Packages')}
        handleTravelerSelectionOnFocus={trackTypeaheadFocus('Packages')}
        handleTrackingClick={trackClick('Packages')}
      />
    case CRUISE_PRODUCT_TYPE:
      return <Cruises {...props}
        handleSelectOnFocus={trackSelectFocus('Cruises')}
        handleInputOnFocus={trackTypeaheadFocus('Cruises')}
        handleTrackingClick={trackClick('Cruises')}
        referral={referral}
      />
    case HOTEL_PRODUCT_TYPE:
    default:
      return (
        <Hotels isStickyInEffect={isStickyInEffect}
          handleTravelerSelectionOnFocus={trackTypeaheadFocus('Hotels')}
          {...props} />
      )
  }
}
SearchForms.propTypes = {
  tab: PropTypes.string,
  isStickyInEffect: PropTypes.bool
}

const SearchContainerCard = styled(Card)`
  ${props => props.theme.mediaQueries[2]} {
    min-width: 720px;
  }
`
const SearchProductTabsRouter = withRouter(
  class SearchProductTabs extends React.Component {
    static propTypes = {
      history: PropTypes.object,
      location: PropTypes.object
    }

    constructor (props) {
      super(props)

      this.initialDateValues = {
        startDate: '',
        endDate: ''
      }

      this.state = {
        selectedTab: HOTEL_PRODUCT_TYPE,
        disableLink: true
      }
    }

    componentDidMount () {
      this.setState({ disableLink: false })

      pclnExperimentation.fireImpression('HP_RC_CAL_SINGLE')
      pclnExperimentation.fireImpression('HP_ACTIVE_TAB_TEST') // TODO: HP_ACTIVE_TAB_TEST test clean up

      if (window.history && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual'
      }
    }

    handleSubmit = url => {
      window.open(url)
    }

    handleChangeSearchForms = (current, next) => {
      const tab = parseQuery(window.location)

      if (!equals(current.values, next.values)) {
        switch (tab) {
          case 'hotels':
            this.setState({ hotelFormSavedValues: next.values })
            break
          case 'cars':
            this.setState({ carsFormSavedValues: next.values })
            break
          case 'flights':
            this.setState({ flightsFormSavedValues: next.values })
            break
          case 'vacations':
            this.setState({ packagesFormSavedValues: next.values })
            break
          default:
            break
        }
      }
    }

    handleClick = e => {
      // handle side effects of clicks
      const { tab, id } = e.target.dataset
      if (tab) {
        PCLNAnalytics.send({
          action: 'Tab',
          data: id
        })
      }
    }

    initValues = (savedValues, initialProductValues = {}, initialDateValues = {}) => {
      if (!savedValues || isEmpty(savedValues)) {
        return initialProductValues
      } else {
        const newValues = { ...savedValues }
        if (initialDateValues.startDate) {
          newValues.startDate = initialDateValues.startDate
        }
        if (initialDateValues.endDate) {
          newValues.endDate = initialDateValues.endDate
        }
        return newValues
      }
    }

    render () {
      const { location, history } = this.props
      const tab = parseQuery(location)
      return (
        <SearchContainerCard
          borderWidth={0} borderRadius={6} boxShadowSize='md' color='background.lightest'
          width={[1, null, null, '70%']}
        >
          <SearchBoxHeader currentTab={tab} />
          <SearchForms
            tab={tab}
            onChange={this.handleChangeSearchForms}
            onSubmit={this.handleSubmit}
            location={location}
            history={history}
            cacheKey={tab}
            initialDateValues={this.initialDateValues}
            enableReinitialize
            hotelFormSavedValues={this.state.hotelFormSavedValues}
            carsFormSavedValues={this.state.carsFormSavedValues}
            packagesFormSavedValues={this.state.packagesFormSavedValues}
            initValues={this.initValues}
            role='tabpanel'
            id={tab}
            ariaLabelledby={tab + '_tabpanel'}
          />
        </SearchContainerCard>
      )
    }
  }
)

SearchProductTabsRouter.WrappedComponent.contextType = ExperimentContext
export default SearchProductTabsRouter
